import { Grid3X3, Users, FileText, FileSignature, Calendar, LayoutDashboard, Mail, BarChart3, ClipboardCheck, Shield, User, CalendarDays, UserCircle, MessageSquarePlus, Mic2, ListMusic, DollarSign, Telescope, Megaphone, Newspaper, Send, Share2, BookOpen, Target, Handshake, Bot, Activity } from "lucide-react";
import type { LucideIcon } from "lucide-react";

// Task #102 — Workspace type discriminator. Kept in sync with
// `WorkspaceType` from shared/schema.ts; duplicated locally so this nav
// module stays free of server-only imports.
export type WorkspaceTypeForNav = "tenant" | "saas";

export interface NavItem {
  id: string;
  title: string;
  url: string;
  icon: LucideIcon;
  permissionKey: string;
  badgeKey?: string;
  // Task #102 — Hide this nav entry when the active workspace is of
  // any of these types. Entertainment-only modules (Leads, Schedule,
  // Talent, etc.) set this to ["saas"] so the Clarik-the-company
  // workspace doesn't show them. Omitted = visible on every type.
  hideOnWorkspaceType?: WorkspaceTypeForNav[];
  // Task #102 — Inverse of hideOnWorkspaceType: only show on these
  // types. Used by saas-only modules like /customers.
  showOnlyOnWorkspaceType?: WorkspaceTypeForNav[];
}

export const TOP_NAV_ITEMS: NavItem[] = [
  {
    id: "profiles",
    title: "Profiles",
    url: "/profiles",
    icon: UserCircle,
    permissionKey: "profiles.module.visible",
  },
  {
    id: "communications",
    title: "Communications",
    url: "/email-hub",
    icon: Mail,
    permissionKey: "communications.module.visible",
    // Communications is genuinely useful for the Clarik sales workspace too
    // (drip to prospects, support inbox), so it stays visible on saas.
  },
  {
    id: "calendarHub",
    title: "Calendar Hub",
    url: "/calendars",
    icon: CalendarDays,
    permissionKey: "calendarHub.module.visible",
  },
  {
    id: "approvals",
    title: "Approvals",
    url: "/intake",
    icon: ClipboardCheck,
    permissionKey: "approvals.module.visible",
    badgeKey: "approvalCount",
  },
  {
    id: "leadGen",
    title: "Lead Gen",
    url: "/lead-gen",
    icon: Telescope,
    permissionKey: "leadGen.module.visible",
    // Lead Gen is itself a sales tool — Clarik's own sales team will use
    // it to find prospect venues/entertainers to pitch. Stays visible.
  },
  {
    id: "schedule",
    title: "Master Schedule",
    url: "/master-schedule",
    icon: Calendar,
    permissionKey: "schedule.module.visible",
  },
  {
    id: "reports",
    title: "Reports",
    url: "/reports",
    icon: BarChart3,
    permissionKey: "reports.module.visible",
    // Reports is repurposed in Clarik workspace to show sales/MRR dashboards,
    // so it stays visible on saas; the saas-specific report widgets are
    // gated inside the page itself.
  },
];

export const SIDEBAR_NAV_ITEMS: NavItem[] = [
  {
    id: "dashboard",
    title: "Dashboard",
    url: "/",
    icon: LayoutDashboard,
    permissionKey: "dashboard.module.visible",
  },
  {
    id: "leads",
    title: "Leads",
    url: "/leads",
    icon: Grid3X3,
    permissionKey: "leads.module.visible",
    // Leads on a saas workspace are sales prospects, which we DO want
    // visible. Keep this entry; only entertainment-domain modules are
    // hidden on saas.
  },
  {
    id: "customers",
    title: "Customers",
    url: "/customers",
    icon: Users,
    permissionKey: "always_visible",
    // Task #102 — Clarik-only module: list of platform_customers. Hidden
    // on every normal tenant workspace.
    showOnlyOnWorkspaceType: ["saas"],
  },
  {
    id: "clients",
    title: "Clients",
    url: "/clients",
    icon: Users,
    permissionKey: "clients.module.visible",
  },
  {
    id: "forms",
    title: "Documents",
    url: "/forms",
    icon: FileSignature,
    permissionKey: "forms.module.visible",
  },
  {
    id: "invoices",
    title: "Invoices",
    url: "/invoices",
    icon: FileText,
    permissionKey: "invoices.module.visible",
    // Kept on saas: Clarik issues its own subscription invoices via the
    // same module; subscription-invoice rendering will layer in later.
  },
  {
    id: "billing",
    title: "Billing",
    url: "/billing",
    icon: DollarSign,
    permissionKey: "billing.module.visible",
    // Kept on saas: Clarik's own billing / Stripe portal lives here.
  },
  // Knowledge Center — hidden from the main sidebar via the platform
  // "Enhancements" page (platform-enhancements.tsx). Routes still exist
  // so direct links continue to resolve for admins/testing.
  // {
  //   id: "knowledge",
  //   title: "Knowledge",
  //   url: "/knowledge",
  //   icon: BookOpen,
  //   permissionKey: "knowledge.module.visible",
  // },
];

// v2: Marketing section hidden until promoted in the super admin panel.
// All routes, APIs, and data remain intact — un-comment to restore.
// Task #205 — Sponsor Hub is the first item in Marketing; it is always
// listed here but the sidebar hides it when useSponsorHubAccess returns false.
export const MARKETING_NAV_ITEMS: NavItem[] = [
  {
    id: "sponsorHub",
    title: "Sponsor Hub",
    url: "/sponsor-hub",
    icon: Handshake,
    permissionKey: "marketing.feature.viewSponsorHub",
  },
  // {
  //   id: "marketingContacts",
  //   title: "Contacts",
  //   url: "/marketing/contacts",
  //   icon: UserCircle,
  //   permissionKey: "marketing.feature.viewContacts",
  // },
  // {
  //   id: "marketingNewsletter",
  //   title: "Newsletter",
  //   url: "/marketing/newsletter",
  //   icon: Newspaper,
  //   permissionKey: "marketing.feature.viewNewsletter",
  // },
  // {
  //   // Task #705 — Ad Manager Phase 1.
  //   id: "marketingAds",
  //   title: "Ad Manager",
  //   url: "/marketing/ads",
  //   icon: Megaphone,
  //   permissionKey: "marketing.feature.viewAds",
  // },
  // {
  //   id: "marketingSocial",
  //   title: "Social Media",
  //   url: "/marketing/social",
  //   icon: Share2,
  //   permissionKey: "socialMedia.module.visible",
  // },
];

export const ARTIST_NAV_ITEMS: NavItem[] = [
  {
    id: "myProfile",
    title: "My Profile",
    url: "/my-profile",
    icon: Mic2,
    permissionKey: "myProfile.module.visible",
  },
  {
    id: "myEvents",
    title: "My Events",
    url: "/events",
    icon: ListMusic,
    permissionKey: "myEvents.module.visible",
  },
];

// Task #419 — Talent module. A top-level, permission-gated nav group that
// promotes the scattered talent surfaces (call sheets, contacts, calendar,
// plus the existing My Events / My Profile pages) into one section. The
// group wrapper is gated on `talent.module.visible`; each item is gated on
// its own key. OFF by default for every role except admin (see registry).
// "show" === "event" everywhere — these are event surfaces.
export const TALENT_NAV_ITEMS: NavItem[] = [
  {
    id: "talentCallsheets",
    title: "Callsheets",
    url: "/talent/callsheets",
    icon: ClipboardCheck,
    permissionKey: "talent.feature.callsheets",
  },
  {
    id: "talentContacts",
    title: "Contacts",
    url: "/talent/contacts",
    icon: Users,
    permissionKey: "talent.feature.contacts",
  },
  {
    id: "talentCalendar",
    title: "Calendar",
    url: "/talent/calendar",
    icon: CalendarDays,
    permissionKey: "talent.feature.calendar",
  },
  {
    id: "myEvents",
    title: "My Events",
    url: "/events",
    icon: ListMusic,
    permissionKey: "myEvents.module.visible",
  },
  {
    id: "myProfile",
    title: "My Profile",
    url: "/my-profile",
    icon: Mic2,
    permissionKey: "myProfile.module.visible",
  },
];

// Enterprise+ — tenant-workspace testing-ground category. The whole group is
// gated on `enterprisePlus.module.visible`, an entitlement injected by
// /api/permissions/effective that is true only when the workspace is on the
// `enterprise_plus` tier. AI Agents (Lead Outreach) was relocated here from the
// top nav; its own `aiAgents.module.visible` entitlement still gates the item.
export const ENTERPRISE_PLUS_NAV_ITEMS: NavItem[] = [
  {
    id: "aiAgents",
    title: "AI Agents",
    url: "/ai-agents",
    icon: Bot,
    permissionKey: "aiAgents.module.visible",
  },
];

export const BETA_NAV_ITEMS: NavItem[] = [
  {
    id: "feedback",
    title: "BetaTest Feedback",
    url: "/feedback",
    icon: MessageSquarePlus,
    permissionKey: "always_visible",
  },
];

export const ADMIN_NAV_ITEMS: NavItem[] = [
  // v2: Manager Panel hidden until feature is promoted in the super admin panel
  // {
  //   id: "managerPanel",
  //   title: "Manager Panel",
  //   url: "/manager-panel",
  //   icon: Target,
  //   permissionKey: "managerPanel.module.visible",
  //   badgeKey: "approvalsInboxCount",
  // },
  {
    id: "adminPanel",
    title: "Admin Panel",
    url: "/admin",
    icon: Shield,
    permissionKey: "adminPanel.module.visible",
  },
  // Task #113 — Super Admin Panel: consolidated /platform/* surfaces
  // rendered as in-page tabs. Visible only on the Clarik (saas) workspace;
  // the page itself enforces super_admin via /api/platform/me.
  {
    id: "superAdminPanel",
    title: "Super Admin Panel",
    url: "/super-admin",
    icon: Shield,
    permissionKey: "always_visible",
    showOnlyOnWorkspaceType: ["saas"],
  },
];

export interface RoutePermission {
  path: string;
  permissionKey: string;
  exact?: boolean;
}

export const ROUTE_PERMISSIONS: RoutePermission[] = [
  { path: "/", permissionKey: "dashboard.module.visible", exact: true },
  { path: "/my-progress", permissionKey: "dashboard.module.visible" },
  { path: "/leads", permissionKey: "leads.module.visible" },
  { path: "/clients", permissionKey: "clients.module.visible" },
  { path: "/forms", permissionKey: "forms.module.visible" },
  { path: "/invoices", permissionKey: "invoices.module.visible" },
  { path: "/billing", permissionKey: "billing.module.visible" },
  { path: "/master-schedule", permissionKey: "schedule.module.visible" },
  { path: "/calendars", permissionKey: "calendarHub.module.visible" },
  { path: "/email-hub", permissionKey: "communications.module.visible" },
  { path: "/intake", permissionKey: "approvals.module.visible" },
  { path: "/reports", permissionKey: "reports.module.visible" },
  { path: "/my-profile", permissionKey: "myProfile.module.visible" },
  { path: "/events", permissionKey: "myEvents.module.visible" },
  { path: "/schedule", permissionKey: "myEvents.module.visible" },
  { path: "/talent/callsheets", permissionKey: "talent.feature.callsheets" },
  { path: "/talent/contacts", permissionKey: "talent.feature.contacts" },
  { path: "/talent/calendar", permissionKey: "talent.feature.calendar" },
  { path: "/admin/team-activity", permissionKey: "adminPanel.feature.activityTracker" },
  { path: "/admin", permissionKey: "adminPanel.module.visible" },
  { path: "/manager-panel", permissionKey: "managerPanel.module.visible" },
  { path: "/settings", permissionKey: "dashboard.module.visible" },
  { path: "/profiles", permissionKey: "profiles.module.visible" },
  { path: "/import", permissionKey: "leads.feature.import" },
  { path: "/lead-gen", permissionKey: "leadGen.module.visible" },
  { path: "/marketing/contacts", permissionKey: "marketing.feature.viewContacts" },
  { path: "/marketing/newsletter", permissionKey: "marketing.feature.viewNewsletter" },
  { path: "/marketing/distribution", permissionKey: "marketing.feature.viewDistribution" },
  { path: "/marketing/ads", permissionKey: "marketing.feature.viewAds" },
  { path: "/marketing/social", permissionKey: "socialMedia.module.visible" },
  { path: "/knowledge", permissionKey: "knowledge.module.visible" },
  { path: "/ai-agents", permissionKey: "aiAgents.module.visible" },
  // Task #113 — Super Admin Panel route. The page enforces super_admin
  // via /api/platform/me; permission system treats it as always visible.
  { path: "/super-admin", permissionKey: "always_visible" },
];

export function getPermissionKeyForRoute(path: string): string | null {
  const exact = ROUTE_PERMISSIONS.find((r) => r.exact && r.path === path);
  if (exact) return exact.permissionKey;

  const prefix = ROUTE_PERMISSIONS.find(
    (r) => !r.exact && path.startsWith(r.path)
  );
  return prefix?.permissionKey || null;
}

// Task #102 — Combined gate for permissions + workspace type. Both must
// pass for a nav item to render. `always_visible` short-circuits the
// permission check (used by Clarik-only modules that don't have a
// per-tenant permission key).
function passesWorkspaceTypeGate(item: NavItem, workspaceType?: WorkspaceTypeForNav): boolean {
  if (!workspaceType) return true;
  if (item.hideOnWorkspaceType?.includes(workspaceType)) return false;
  if (item.showOnlyOnWorkspaceType && !item.showOnlyOnWorkspaceType.includes(workspaceType)) {
    return false;
  }
  return true;
}

function passesPermissionGate(item: NavItem, permissions: Record<string, boolean>): boolean {
  if (item.permissionKey === "always_visible") return true;
  return permissions[item.permissionKey] === true;
}

export function getVisibleTopNavItems(
  permissions: Record<string, boolean>,
  workspaceType?: WorkspaceTypeForNav,
): NavItem[] {
  return TOP_NAV_ITEMS.filter(
    (item) => passesPermissionGate(item, permissions) && passesWorkspaceTypeGate(item, workspaceType),
  );
}

export function getVisibleSidebarNavItems(
  permissions: Record<string, boolean>,
  workspaceType?: WorkspaceTypeForNav,
): NavItem[] {
  return SIDEBAR_NAV_ITEMS.filter(
    (item) => passesPermissionGate(item, permissions) && passesWorkspaceTypeGate(item, workspaceType),
  );
}

export function getVisibleAdminNavItems(
  permissions: Record<string, boolean>,
  workspaceType?: WorkspaceTypeForNav,
): NavItem[] {
  return ADMIN_NAV_ITEMS.filter(
    (item) => passesPermissionGate(item, permissions) && passesWorkspaceTypeGate(item, workspaceType),
  );
}

export function getVisibleArtistNavItems(
  permissions: Record<string, boolean>,
  workspaceType?: WorkspaceTypeForNav,
): NavItem[] {
  return ARTIST_NAV_ITEMS.filter(
    (item) => passesPermissionGate(item, permissions) && passesWorkspaceTypeGate(item, workspaceType),
  );
}

export function getVisibleTalentNavItems(
  permissions: Record<string, boolean>,
  workspaceType?: WorkspaceTypeForNav,
): NavItem[] {
  // The group is only meaningful when the module itself is enabled.
  if (permissions["talent.module.visible"] !== true) return [];
  return TALENT_NAV_ITEMS.filter(
    (item) => passesPermissionGate(item, permissions) && passesWorkspaceTypeGate(item, workspaceType),
  );
}

export function getVisibleEnterprisePlusNavItems(
  permissions: Record<string, boolean>,
  workspaceType?: WorkspaceTypeForNav,
): NavItem[] {
  // The whole category is gated on the enterprise_plus tier entitlement.
  if (permissions["enterprisePlus.module.visible"] !== true) return [];
  return ENTERPRISE_PLUS_NAV_ITEMS.filter(
    (item) => passesPermissionGate(item, permissions) && passesWorkspaceTypeGate(item, workspaceType),
  );
}

export function getAllModulePermissionKeys(): string[] {
  const allItems = [...TOP_NAV_ITEMS, ...SIDEBAR_NAV_ITEMS, ...ARTIST_NAV_ITEMS, ...TALENT_NAV_ITEMS, ...ENTERPRISE_PLUS_NAV_ITEMS, ...ADMIN_NAV_ITEMS];
  // 'always_visible' is a sentinel for saas-only modules with no
  // per-tenant permission row; exclude it from the permission catalog.
  return Array.from(
    new Set(allItems.map((item) => item.permissionKey).filter((k) => k !== "always_visible")),
  );
}
